import { useState, useCallback, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAppStore } from '@/store/useAppStore'

const TOUR_KEY = 'prjct-onboarding-v1'
const WELCOME_KEY = 'prjct-onboarding-v2-seen'
const CARD_W = 320
const SPOT_PAD = 7
const CARD_GAP = 14

const STEPS = [
  {
    id: 'welcome',
    target: null,
    placement: 'center',
    title: 'Welcome to PRJCT',
    body: "Your agency's project management hub. This tour covers everything you need — takes about a minute.",
  },
  {
    id: 'sidebar',
    target: '[data-tour="sidebar"]',
    placement: 'right',
    title: 'Navigate your workspace',
    body: 'Projects live in the sidebar. Click any project to open it. My Tasks shows all tasks assigned to you across every project.',
  },
  {
    id: 'views',
    target: '[data-tour="view-tabs"]',
    placement: 'bottom',
    title: '5 views, one project',
    body: 'Switch between List, Board, Timeline, Calendar, and Dashboard. Each shows the same tasks differently. Press 1–5 on your keyboard to jump instantly.',
  },
  {
    id: 'tasks',
    target: '[data-tour="add-task"]',
    placement: 'bottom-left',
    title: 'Creating & managing tasks',
    body: 'Add a task here or use the inline + in any list or board column. Click any task row to open full details — priority, due date, subtasks, comments, and more.',
  },
  {
    id: 'shortcuts',
    target: null,
    placement: 'center',
    title: 'Work faster',
    shortcuts: [
      { key: '⌘K', label: 'Search everything' },
      { key: '⌘Z', label: 'Undo any action' },
      { key: '⌘/', label: 'View all shortcuts' },
      { key: '1–5', label: 'Switch views instantly' },
    ],
  },
]

function calcSpotlight(selector) {
  if (!selector) return null
  const el = document.querySelector(selector)
  if (!el) return null
  const r = el.getBoundingClientRect()
  return {
    left: r.left - SPOT_PAD,
    top: r.top - SPOT_PAD,
    width: r.width + SPOT_PAD * 2,
    height: r.height + SPOT_PAD * 2,
  }
}

function calcCardPos(spot, placement) {
  const vw = window.innerWidth
  const vh = window.innerHeight

  if (!spot || placement === 'center') {
    return { left: Math.round((vw - CARD_W) / 2), top: Math.round(vh * 0.38) }
  }

  const { left, top, width, height } = spot

  if (placement === 'right') {
    return {
      left: Math.min(left + width + CARD_GAP, vw - CARD_W - 16),
      top: Math.max(16, Math.min(top + height / 2 - 130, vh - 300 - 16)),
    }
  }

  if (placement === 'bottom') {
    return {
      left: Math.max(16, Math.min(vw - CARD_W - 16, Math.round(left + width / 2 - CARD_W / 2))),
      top: top + height + CARD_GAP,
    }
  }

  if (placement === 'bottom-left') {
    return {
      left: Math.max(16, Math.min(vw - CARD_W - 16, Math.round(left + width - CARD_W))),
      top: top + height + CARD_GAP,
    }
  }

  return { left: Math.round((vw - CARD_W) / 2), top: Math.round(vh * 0.38) }
}

export function OnboardingTour() {
  const projects = useAppStore((s) => s.projects)
  const lastSyncAt = useAppStore((s) => s._lastSyncAt)
  const [welcomeDone, setWelcomeDone] = useState(() => !!localStorage.getItem(WELCOME_KEY))
  const [done, setDone] = useState(() => !!localStorage.getItem(TOUR_KEY))
  const [step, setStep] = useState(0)
  const [spot, setSpot] = useState(null)
  const [cardPos, setCardPos] = useState(null)

  // Auto-skip the tour for returning users — anyone who already has projects has clearly
  // figured the app out. Persist so they don't see it on later reloads either.
  useEffect(() => {
    if (!done && projects.length > 0) {
      localStorage.setItem(TOUR_KEY, '1')
      setDone(true)
    }
  }, [projects.length, done])

  const cur = STEPS[step]

  const recalc = useCallback(() => {
    const s = calcSpotlight(cur.target)
    setSpot(s)
    setCardPos(calcCardPos(s, cur.placement))
  }, [cur])

  // useEffect (not useLayoutEffect) + delay so conditionally-rendered targets
  // like view-tabs and add-task have time to appear in the DOM
  useEffect(() => {
    if (done) return
    const tid = setTimeout(recalc, 80)
    return () => clearTimeout(tid)
  }, [done, recalc])

  useEffect(() => {
    if (done) return
    window.addEventListener('resize', recalc)
    return () => window.removeEventListener('resize', recalc)
  }, [done, recalc])

  useEffect(() => {
    if (welcomeDone) return
    function onWelcomeDone() { setWelcomeDone(true) }
    window.addEventListener('prjct:welcome-done', onWelcomeDone)
    return () => window.removeEventListener('prjct:welcome-done', onWelcomeDone)
  }, [welcomeDone])

  useEffect(() => {
    if (done) return
    function block(e) { e.preventDefault() }
    // block scroll while tour is open
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [done])

  function next() {
    if (step < STEPS.length - 1) {
      setStep((s) => s + 1)
    } else {
      finish()
    }
  }

  function finish() {
    localStorage.setItem(TOUR_KEY, '1')
    setDone(true)
  }

  // Same render-time guards as OnboardingWelcome: short-circuit on the same render that
  // projects populate, and never render before the first sync (we don't know yet).
  if (!welcomeDone || done || !cardPos || lastSyncAt === 0 || projects.length > 0) return null

  const isLast = step === STEPS.length - 1

  const cardStyle = {
    position: 'fixed',
    left: cardPos.left,
    top: cardPos.top,
    width: CARD_W,
    zIndex: 62,
    background: 'rgba(22, 22, 28, 0.97)',
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    border: '1px solid rgba(255,255,255,0.09)',
    borderRadius: 18,
    padding: '22px 22px 18px',
    boxShadow: '0 32px 64px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,0,0,0.4)',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  }

  return (
    <div
      className="fixed inset-0 z-[60]"
      role="dialog"
      aria-modal="true"
      aria-label="Onboarding tour"
    >
      {/* Backdrop / spotlight */}
      <AnimatePresence mode="sync">
        {spot ? (
          <motion.div
            key="spotlight"
            initial={{ opacity: 0, left: spot.left, top: spot.top, width: spot.width, height: spot.height }}
            animate={{
              opacity: 1,
              left: spot.left,
              top: spot.top,
              width: spot.width,
              height: spot.height,
            }}
            exit={{ opacity: 0 }}
            transition={{ type: 'spring', duration: 0.38, bounce: 0.08 }}
            style={{
              position: 'absolute',
              borderRadius: 10,
              pointerEvents: 'none',
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.68)',
              zIndex: 60,
            }}
          />
        ) : (
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(0,0,0,0.68)',
              zIndex: 60,
            }}
          />
        )}
      </AnimatePresence>

      {/* Accent ring on spotlight target */}
      <AnimatePresence>
        {spot && (
          <motion.div
            key="ring"
            initial={{ opacity: 0, left: spot.left - 3, top: spot.top - 3, width: spot.width + 6, height: spot.height + 6 }}
            animate={{
              opacity: 1,
              left: spot.left - 3,
              top: spot.top - 3,
              width: spot.width + 6,
              height: spot.height + 6,
            }}
            exit={{ opacity: 0 }}
            transition={{ type: 'spring', duration: 0.38, bounce: 0.08 }}
            style={{
              position: 'absolute',
              borderRadius: 13,
              pointerEvents: 'none',
              border: '1.5px solid rgba(244,136,154,0.5)',
              boxShadow: '0 0 16px rgba(244,136,154,0.2)',
              zIndex: 61,
            }}
          />
        )}
      </AnimatePresence>

      {/* Tour card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 10, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6, scale: 0.98 }}
          transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
          style={cardStyle}
        >
          {/* Step counter + skip */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.3)',
              fontFamily: 'Helvetica Neue, sans-serif',
            }}>
              {step + 1} of {STEPS.length}
            </span>
            <button
              onClick={finish}
              style={{
                fontSize: 11,
                color: 'rgba(255,255,255,0.28)',
                fontFamily: 'Helvetica Neue, sans-serif',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                transition: 'color 150ms ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.55)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.28)')}
            >
              Skip tour
            </button>
          </div>

          {/* Title */}
          <h2 style={{
            fontSize: 17,
            fontWeight: 700,
            color: '#fff',
            fontFamily: 'Helvetica Neue, sans-serif',
            letterSpacing: '-0.015em',
            lineHeight: 1.2,
            margin: 0,
          }}>
            {cur.title}
          </h2>

          {/* Body text */}
          {cur.body && (
            <p style={{
              fontSize: 13,
              color: 'rgba(255,255,255,0.6)',
              fontFamily: 'Helvetica Neue, sans-serif',
              lineHeight: 1.6,
              margin: 0,
            }}>
              {cur.body}
            </p>
          )}

          {/* Shortcuts list */}
          {cur.shortcuts && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              {cur.shortcuts.map((s) => (
                <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <kbd style={{
                    fontFamily: 'Helvetica Neue, sans-serif',
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'rgba(255,255,255,0.88)',
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 6,
                    padding: '3px 9px',
                    letterSpacing: '0.01em',
                    whiteSpace: 'nowrap',
                    minWidth: 48,
                    textAlign: 'center',
                    display: 'inline-block',
                  }}>
                    {s.key}
                  </kbd>
                  <span style={{
                    fontSize: 13,
                    color: 'rgba(255,255,255,0.55)',
                    fontFamily: 'Helvetica Neue, sans-serif',
                  }}>
                    {s.label}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Footer: progress dots + next button */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 2 }}>
            {/* Pill dots */}
            <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              {STEPS.map((_, i) => (
                <div
                  key={i}
                  style={{
                    width: i === step ? 18 : 6,
                    height: 6,
                    borderRadius: 3,
                    background: i === step ? '#e63946' : 'rgba(255,255,255,0.18)',
                    transition: 'width 280ms cubic-bezier(0.23,1,0.32,1), background 250ms ease',
                  }}
                />
              ))}
            </div>

            {/* Next / Get started button */}
            <button
              onClick={next}
              style={{
                fontFamily: 'Helvetica Neue, sans-serif',
                fontSize: 13,
                fontWeight: 600,
                color: '#000',
                background: '#e63946',
                border: 'none',
                borderRadius: 10,
                padding: '8px 18px',
                cursor: 'pointer',
                transition: 'transform 120ms ease-out, opacity 120ms ease-out',
                lineHeight: 1,
                letterSpacing: '-0.01em',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.88')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
              onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.97)')}
              onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              {isLast ? 'Get started →' : 'Next →'}
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  )
}
